import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Alert, Button, Checkbox, Divider, Popconfirm } from "antd";
import { ColumnsType } from "antd/es/table";
import { Table, TablePaginationConfig } from "antd/lib";
import {
  AllInfoForProctorI,
  ProctorTableI,
} from "../../../interfaces/proctors.interface";
import { useRole } from "../../../hooks/user-roles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { LIMIT } from "../../../constants/pagination.constant";
import {
  deleteProctorSelectedFile,
  getProctorsfullRecords,
  proctorConfirmation,
} from "../../../services/proctors.service";
import Message from "../../shared/Message/Message";
import { AxiosError } from "axios";
import CustomModal from "../../shared/Modal/Modal";
import ProctorDeclarationModal from "./proctor-declaration-modal";
import { usePDF } from "react-to-pdf";
import { downloadAssignmentFile } from "../../../services/assignments.service";

interface TableParams {
  pagination?: TablePaginationConfig;
}

const ProctorProfileTable = () => {
  const [proctorSelected, setProctorSelected] = useState<
    ProctorTableI | undefined
  >();
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
  const [openDeclaration, setOpenDeclaration] = useState<boolean>(false);
  const [agree, setAgree] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { toPDF, targetRef } = usePDF({
    filename: "Declaration.pdf",
    page: { margin: 15 },
  });
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: LIMIT,
    },
  });
  const { user } = useRole();
  const mutationOnError = (err: AxiosError) => {
    if (err.code === "ERR_BAD_REQUEST") {
      Message().error("The information provided is not right");
    } else {
      Message().error(err.message);
    }
  };
  const proctorAcceptance = useMutation({
    mutationFn: (proctorInfo: ProctorTableI) => {
      const objData = {
        proctorSelectedId: proctorInfo.id,
        proctorAccepted: agree ? "yes" : "no",
      };
      return proctorConfirmation(objData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`proctor-profile-${user?.id}-all-info`],
      });
      Message().success(
        "You have submitted your proctor acceptance successfully! You will recive an email with futher instructions.",
      );
      setOpenConfirmModal(false);
    },
    onError: mutationOnError,
  });

  const deleteProctorSelectedFiles = useMutation({
    mutationFn: (fileId: number) => {
      return deleteProctorSelectedFile(fileId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`proctor-profile-${user?.id}-all-info`],
      });
      Message().success("Exam file has been deleted");
    },
  });

  const columns: ColumnsType<ProctorTableI> = [
    {
      title: "Student Information",
      onHeaderCell: () => ({ style: { backgroundColor: "rgb(186 230 253)" } }),
      children: [
        {
          title: "First Name",
          dataIndex: "firstName",
          onHeaderCell: () => ({
            style: { backgroundColor: "rgb(186 230 253)" },
          }),
          key: "firstName",
        },
        {
          title: "Last Name",
          dataIndex: "lastName",
          onHeaderCell: () => ({
            style: { backgroundColor: "rgb(186 230 253)" },
          }),
          key: "lastName",
        },
      ],
    },
    {
      title: "Exam Information",
      onHeaderCell: () => ({
        style: { backgroundColor: "rgb(226 232 240)" },
      }),
      children: [
        {
          title: "Course",
          dataIndex: "courseCode",
          key: "courseCode",
          onHeaderCell: () => ({
            style: { backgroundColor: "rgb(226 232 240)" },
          }),
        },
        {
          title: "Assignment Type",
          dataIndex: "assignmentType",
          key: "assignmentType",
          onHeaderCell: () => ({
            style: { backgroundColor: "rgb(226 232 240)" },
          }),
        },
        {
          title: "Date",
          dataIndex: "date",
          key: "date",
          onHeaderCell: () => ({
            style: { backgroundColor: "rgb(226 232 240)" },
          }),
        },
        {
          title: "Exam length (hrs)",
          dataIndex: "durationTime",
          key: "durationTime",
          onHeaderCell: () => ({
            style: { backgroundColor: "rgb(226 232 240)" },
          }),
        },
        {
          title: "Proctor Acceptance",
          render: (val: string, record: ProctorTableI) =>
            record.proctorAccepted !== "yes" ? (
              <Popconfirm
                title="Accept Student?"
                description="Are you sure?"
                className="!max-w-52"
                onConfirm={() => {
                  setOpenConfirmModal(true);
                  setProctorSelected(record);
                }}
              >
                <Button type="primary">Accept Role</Button>
              </Popconfirm>
            ) : (
              <CheckOutlined />
            ),
          onHeaderCell: () => ({
            style: { backgroundColor: "rgb(226 232 240)" },
          }),
        },
        {
          title: "Download Exam",
          render: (val: string, record: ProctorTableI) => {
            const files = record.assignment?.files ?? [];
            let examFileName = "";
            let examId = 0;
            if (!record.assignment || files.length <= 0) {
              return "No File";
            }
            if (files) {
              examFileName = files[0].name ?? files[0].url;
              examId = files[0].id;
            }
            return (
              record.assignment?.close !== 1 &&
              record.instructorAccepted === "yes" && (
                <Button
                  type="primary"
                  href={record.examUrl}
                  onClick={() => {
                    downloadAssignmentFile(examId, examFileName);
                  }}
                >
                  Download
                </Button>
              )
            );
          },
          onHeaderCell: () => ({
            style: { backgroundColor: "rgb(226 232 240)" },
          }),
        },
        {
          title: "Submit Written Exam",
          render: (val: string, record: ProctorTableI) => {
            if (
              record.assignment?.close === 1 ||
              record.instructorAccepted !== "yes"
            ) {
              return null;
            }
            return record.instructorAccepted === "yes" && record.examUrl ? (
              <Popconfirm
                title="You are deleting an exam"
                description="you will be able to submit another written exam file"
                onConfirm={() => {
                  deleteProctorSelectedFiles.mutate(record.id);
                }}
              >
                <Button
                  type="primary"
                  onClick={() => {}}
                  danger
                  className=" hover:!bg-yellow-300"
                >
                  Delete Exam
                </Button>
              </Popconfirm>
            ) : (
              <Button
                type="primary"
                onClick={() => {
                  setProctorSelected(record);
                  setOpenDeclaration(!openDeclaration);
                }}
                className="!bg-yellow-500 hover:!bg-yellow-300"
              >
                Submit Exam
              </Button>
            );
          },
          onHeaderCell: () => ({
            style: { backgroundColor: "rgb(226 232 240)" },
          }),
        },
      ],
    },
  ];
  const { data } = useQuery({
    queryKey: [`proctor-profile-${user?.id}-all-info`],
    queryFn: async () => {
      const limit = tableParams.pagination?.pageSize ?? LIMIT;
      const skip = ((tableParams.pagination?.current ?? 1) - 1) * limit;
      const response = await getProctorsfullRecords({
        staffId: Number(user?.id),
        limit,
        skip,
      });
      if (response.count) {
        setTableParams((current) => ({
          pagination: { ...current.pagination, total: response.count },
        }));
      }
      return response;
    },
    select: ({ list, count }) => ({
      count,
      list: list.map((info: AllInfoForProctorI) => ({
        ...info,
        key: info.id,
        firstName: info.studentCourse.user.firstName,
        lastName: info.studentCourse.user.lastName,
        assignmentType: info.assignment.evaluationType,
        courseCode: info.assignment?.course?.code ?? "",
      })),
    }),
  });
  const onSubmit = () => {
    if (proctorSelected) {
      proctorAcceptance.mutate(proctorSelected);
    }
  };
  return (
    <>
      <Table
        columns={columns}
        dataSource={data?.list}
        pagination={data && data?.count >= LIMIT && tableParams.pagination}
      />
      <CustomModal
        title={"Proctor Acceptance Form"}
        open={openConfirmModal}
        onClose={() => {
          setOpenConfirmModal(!openConfirmModal);
        }}
      >
        <div ref={targetRef}>
          <h1 className="text-center">
            SEABC Certificate in Structural Engineering Program Proctor
            Acceptance Form
          </h1>
          <p>
            <b>Course : </b>{" "}
            {`${proctorSelected?.assignment?.course?.code} ${proctorSelected?.assignment?.course?.name}`}
          </p>
          <p>
            <b>Term : </b> {proctorSelected?.assignment?.course?.term}
          </p>
          <p>
            <b>Course coordinator : </b>{" "}
            {`${proctorSelected?.assignment?.courseStaff?.user?.firstName} ${proctorSelected?.assignment?.courseStaff?.user?.lastName}`}
          </p>
          <p>
            <b>Student’s Name : </b>
            {`${proctorSelected?.studentCourse?.user.firstName} ${proctorSelected?.studentCourse?.user.lastName}`}
          </p>
          <Divider />
          <p>
            Your colleague is currently enrolled in this course remotely through
            webcasting. It will not be possible form him/her to write the final
            examination with the class.
          </p>
          <p>As a proctor, you will:</p>
          <ul>
            <li>Arrange a convenient time and place for the exam</li>
            <li>
              Conduct the exam; hand out the questions, supervise the writing
              and collect the completed exam.
            </li>
            <li>
              Upload the completed exam through your ‘Proctor Profile’ page with
              the submission code provided by the student.
            </li>
          </ul>
          <p>
            As it may be difficult for you to conduct the exam at the same time
            and date as the class. It may be written at another time but it is
            preferred that it be written as close to the classroom examination
            date as possible. The venue should be a closed room with no other
            concurrent activities or persons, other than you, in the room.
          </p>
          <p>
            Before confirming. Here are the requirements to be an appropriate
            choice as a proctor. A proctor must:
          </p>
          <ul>
            <li>
              {" "}
              Have standing in a professional association (e.g., EGBC, ASTTBC,
              P.Eng., CPA, etc.)
            </li>
            <li>Not be related to the student</li>
            <li>Not be subordinate to the student in the workplace.</li>
            <li>
              Not be in any relationship to the student to avoid conflict of
              interest.
            </li>
          </ul>
          <Alert
            message="Participation by persons such as yourself greatly assist our webcast
          students and allows our program to serve structural engineers outside
          the Vancouver area!"
            type="warning"
          />
          <div className="w-9/12 text-center m-auto">
            <Divider />
            <Checkbox checked={agree} onChange={() => setAgree(!agree)}>
              By checking this box, I certify that I have carefully read and
              agree with all the above term and points.
            </Checkbox>
            <Divider />
          </div>
        </div>
        <div className="flex space-x-2.5 > * + * justify-center">
          <Button
            type="primary"
            onClick={onSubmit}
            icon={<DownloadOutlined />}
            disabled={!agree}
          >
            Submit
          </Button>
          <Button
            className="box-border"
            type="primary"
            icon={<CloseOutlined />}
            onClick={() => {
              setOpenConfirmModal(!openConfirmModal);
              setAgree(false);
            }}
            danger
          >
            Cancel
          </Button>
          <Button onClick={() => toPDF()} icon={<PrinterOutlined />}>
            Print
          </Button>
        </div>
      </CustomModal>
      {openDeclaration && (
        <ProctorDeclarationModal
          proctorSelected={proctorSelected}
          closeModal={() => setOpenDeclaration(!openDeclaration)}
          openModal={openDeclaration}
        />
      )}
    </>
  );
};

export default ProctorProfileTable;
